/**
 * Video Content Rectangle Utilities
 * Calculates actual video content bounds excluding letterbox/pillarbox areas
 */

export interface ContentRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Calculate the actual video content rectangle within the video element
 * Excludes letterbox (top/bottom bars) and pillarbox (left/right bars)
 * Returns coordinates in CSS pixels relative to video element
 */
export function getContentRect(videoEl: HTMLVideoElement): ContentRect {
  const cw = videoEl.clientWidth;
  const ch = videoEl.clientHeight;
  const vw = videoEl.videoWidth;
  const vh = videoEl.videoHeight;
  
  // Handle edge cases
  if (!vw || !vh || !cw || !ch) {
    return { x: 0, y: 0, w: cw, h: ch };
  }
  
  // Calculate scale to fit video inside container maintaining aspect ratio
  const scale = Math.min(cw / vw, ch / vh);
  const w = vw * scale;
  const h = vh * scale;
  
  // Center the content within the container
  const x = (cw - w) / 2;
  const y = (ch - h) / 2;
  
  return { x, y, w, h };
}

/**
 * Convert content rect to canvas pixel coordinates accounting for devicePixelRatio
 */
export function contentRectToCanvas(
  contentRect: ContentRect, 
  dpr: number = window.devicePixelRatio || 1
): ContentRect {
  return {
    x: contentRect.x * dpr,
    y: contentRect.y * dpr,
    w: contentRect.w * dpr,
    h: contentRect.h * dpr
  };
}
